<template>
  <div class="dwp-space-y-4 dwp-px-6 dwp-py-5" data-cy="task" v-if="edit">
    <div class="dwp-shadow-xl dwp-bg-white dwp-rounded">
      <EditTask class="dwp-rounded dwp-shadow-xs" :title.sync="title" :content.sync="notes" />
    </div>
    <div class="dwp-flex dwp-items-center dwp-mx-5">
      <VButton @click.native="save" data-cy="edit-task-save">Save</VButton>
      <VButton type="plain" @click.native="edit = false">Cancel</VButton>
    </div>
  </div>

  <div v-else class="dwp-px-8 dwp-mx-2 dwp-rounded dwp-relative dwp-group" data-cy="task">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      fill="currentColor"
      v-if="!task.done"
      style="top: 11px; left: 9px;"
      class="dwp-drag-handle dwp-w-5 dwp-cursor-move dwp-text-gray-400 dwp-absolute dwp-left-0 dwp-opacity-0 group-hover:dwp-opacity-100 dwp-transition-opacity dwp-duration-200"
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path
        d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
      />
    </svg>
    <div
      class="dwp-flex dwp-justify-between dwp-items-start dwp-border-t dwp-border-gray-100 dwp-py-3"
    >
      <TaskTemplate>
        <template #checkbox>
          <Checkbox
            data-cy="toggle-done"
            class="dwp-cursor-pointer"
            :checked.sync="task.done"
            @toggle="toggleDone(task)"
          />
        </template>
        <template #title>
          <div
            class="dwp-space-x-2 dwp-drag-handle dwp-cursor-pointer"
            @click="toggleOpen"
            data-cy="toggle-notes"
          >
            <div
              class="dwp-inline"
              data-cy="task-title"
              :class="task.done ? 'dwp-line-through' : ''"
            >
              <slot>{{ task.title }}</slot>
            </div>
            <AlignLeftIcon
              class="dwp-inline-block dwp-w-4 dwp-text-gray-400"
              v-if="hasNotes && task.notes"
            />
          </div>
        </template>
        <template #content v-if="open && hasNotes">
          <div
            data-cy="task-notes"
            class="dwp-editor-content dwp-text-gray-600 dwp-text-sm dwp-leading-normal"
            v-html="task.notes"
          ></div>
        </template>
      </TaskTemplate>
      <div
        class="dwp-mt-1 dwp-flex dwp-items-center dwp-space-x-2 dwp-text-gray-500 dwp-text-sm dwp-opacity-0 group-hover:dwp-opacity-100 dwp-transition-opacity dwp-duration-200"
      >
        <Edit2Icon
          class="dwp-w-4 dwp-h-4 dwp-cursor-pointer"
          style="fill:none"
          data-cy="edit-task"
          @click.prevent="edit = !edit"
        />
        <TrashIcon
          class="dwp-w-4 dwp-h-4 dwp-cursor-pointer"
          style="fill:none"
          data-cy="trash-task"
          @click.prevent="$emit('trash')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { TrashIcon, Edit2Icon, AlignLeftIcon } from "vue-feather-icons";

import VButton from "./ui/VButton.vue";
import TextInput from "./ui/TextInput.vue";
import unescape from "lodash/unescape";
import Checkbox from "./Checkbox.vue";
import EditTask from "./EditTask.vue";
import TaskTemplate from "./templates/TaskTemplate.vue";

import { call } from "vuex-pathify";

export default {
  props: {
    task: Object,
  },
  data() {
    return {
      edit: false,
      title: "",
      notes: "",
      open: false,
    };
  },
  components: {
    VButton,
    TextInput,
    TrashIcon,
    EditTask,
    AlignLeftIcon,
    Checkbox,
    TaskTemplate,
    Edit2Icon,
  },

  mounted() {
    this.title = this.task.title;
    this.notes = this.task.notes;
  },

  computed: {
    hasNotes() {
      // bail for blank content
      if (this?.task?.notes === "<p></p>") {
        return false;
      }

      // if we have rendered content
      if (this?.task?.notes) {
        return true;
      }

      // if we have unsaved content that's not blank
      if (
        typeof this?.task?.notes === "string" &&
        this?.task.notes !== "<p></p>"
      ) {
        return true;
      }
      return false;
    },
  },

  watch: {
    edit(val) {
      if (val) {
        this.$nextTick(() => {
          this?.$refs?.edit?.focus();
        });
      }
    },
  },

  methods: {
    ...call("tasks", ["toggleDone", "editTask"]),
    save() {
      this.editTask({
        task: this.task,
        data: {
          title: this.title,
          notes: this.notes,
        },
      });
      this.edit = false;
    },
    toggleOpen() {
      if (!this.task.notes) {
        return;
      }
      this.open = !this.open;
    },
  },
};
</script>